<script>
  import { CSS_VARS } from '../../../constants';
  import sticky from '../../../directives/sticky/sticky';
  import { getCssVar } from '../../../utils/helpers';
  import Button from '../../Button/Button.vue';
  import Expand from '../../Expand/Expand.vue';
  import Icon from '../../Icon/Icon.vue';

  export default {
    name: 'list-group',

    components: {
      Expand,
      'll-button': Button,
      Icon,
    },

    directives: {
      sticky,
    },

    props: {
      /**
       * Disables the gutters between adjacent ListGroup components.
       */
      disableExternalGutters: {
        type: Boolean,
        default: false,
      },

      /**
       * Allows the list items to be collapsed
       */
      isCollapsible: {
        type: Boolean,
        default: false,
      },

      /**
       * Allows the expanded content to be collapsed by default.
       * Note: has no effect unless the `isCollapsible` prop is `true`.
       */
      isCollapsedByDefault: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      const isExpanded = !this.isCollapsible || !this.isCollapsedByDefault;

      return {
        isExpanded,
        isDoneExpanding: isExpanded,
      };
    },

    computed: {
      headerOffset() {
        return parseFloat(getCssVar(CSS_VARS.TOP_HEADER_HEIGHT));
      },
    },

    methods: {
      toggleExpand() {
        if (!this.isCollapsible) {
          return;
        }

        this.isExpanded = !this.isExpanded;

        if (!this.isExpanded) {
          this.isDoneExpanding = false;
        } // else wait for Expand to emit @after-expand
      },

      onHeaderClick(e) {
        // toggle the ListGroup, except when clicking the actions
        if (!this.$refs.actionsContainer.contains(e.target)) {
          this.toggleExpand();
        }
      },
    },
  };
</script>

<template>
  <div
    :class="[
      $style['list-group'],
      {
        [$style['disable-external-gutters']]: disableExternalGutters,
        [$style['is-collapsed']]: !isExpanded,
      },
    ]"
  >
    <header
      class="flex items-center"
      :class="[$style['main-header'], { 'cursor-pointer': isCollapsible }]"
      @click="onHeaderClick"
    >
      <ll-button v-if="isCollapsible" :class="['mr-1.5 text-ice-900', $style['expansion-toggle-button']]" icon>
        <Icon :class="[isExpanded ? 'rotate-270' : 'rotate-180']" name="chevron-left" />
      </ll-button>

      <div class="flex-auto">
        <slot name="title"></slot>
      </div>

      <div ref="actionsContainer">
        <slot name="actions"></slot>
      </div>
    </header>

    <Expand :is-expanded="isExpanded" @after-expand="isDoneExpanding = true">
      <div class="relative w-full lg:shadow">
        <header
          v-if="$slots['expanded-content-list-header']"
          v-sticky="isExpanded && isDoneExpanding ? headerOffset : 0"
          :class="$style['expanded-content-header']"
        >
          <div class="ll-grid flex-1" :class="$style['expanded-content-header-grid']">
            <slot name="expanded-content-list-header"></slot>
          </div>
        </header>
        <div :class="$style['expanded-content-list-items-wrapper']">
          <transition-group name="fade" mode="out-in">
            <slot name="expanded-content-list-items"></slot>
          </transition-group>
        </div>
      </div>
    </Expand>
  </div>
</template>

<style module>
  @reference "../../../../styles/main.css";

  @layer utilities {
    .main-header {
      @apply shadow;

      background-color: var(--color-white);
      border-radius: var(--radius-sm);
      margin-bottom: --spacing(6);
      padding: var(--grid-gutter);

      @media (width >= theme(--breakpoint-lg)) {
        border-bottom: 1px solid var(--color-ice-200);
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        margin-bottom: 0;
      }
    }

    .expanded-content-header {
      background-color: var(--color-white);
      border-bottom: 1px solid var(--color-ice-200);
      color: var(--color-ice-900);
      display: none;
      font-weight: 500;
      padding: 0 --spacing(3);
      transition: padding 150 var(--ease-swing);
      z-index: 1;

      @media (width >= theme(--breakpoint-lg)) {
        align-items: center;
        display: flex;
      }

      &:global(.is-sticky) {
        @apply shadow;

        border-radius: 0;
        z-index: calc(var(--z-index-control) + 1);
      }
    }

    .expanded-content-header-grid {
      column-gap: 0;
      grid-column-gap: 0;

      > div {
        align-items: center;
        border-right: 1px solid var(--color-ice-200);
        display: flex;
        font-size: var(--text-xs);
        justify-content: space-between;
        padding: --spacing(3);

        &:last-of-type {
          border-right: 0;
        }
      }
    }

    .expansion-toggle-button:focus {
      box-shadow: none;
    }

    .list-group:global(.disable-external-gutters) {
      .main-header {
        @media (width >= theme(--breakpoint-lg)) {
          border-radius: 0;
        }
      }

      .expanded-content-list-items-wrapper > * {
        @media (width >= theme(--breakpoint-lg)) {
          border-bottom: 1px solid var(--color-ice-200);
          border-radius: 0;
        }
      }

      &:last-of-type {
        &:global(.is-collapsed) .main-header,
        .expanded-content-list-items-wrapper > *:last-child {
          @media (width >= theme(--breakpoint-lg)) {
            border-bottom: none;
            border-bottom-left-radius: var(--radius-sm);
            border-bottom-right-radius: var(--radius-sm);
          }
        }
      }
    }
  }
</style>
